import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import DomainDebugPanel from './DomainDebugPanel';
import StepVisualizer from './StepVisualizer';
import DNSVerificationTracker from './DNSVerificationTracker';

interface Domain {
  id: number;
  domain: string;
  verification_status: string;
  dkim_enabled: boolean;
  mail_from_domain: string;
  default_from_email: string;
  bounce_rate: number;
  complaint_rate: number;
  created_at: string;
  dnsRecords?: DNSRecord[];
  allRecordsVerified?: boolean;
}

interface DNSRecord {
  id?: string;
  type?: 'TXT' | 'CNAME' | 'MX' | 'A' | 'AAAA' | 'NS';
  record_type?: string;
  name?: string;
  record_name?: string;
  value?: string;
  record_value?: string;
  ttl: number;
  priority?: number;
  is_verified?: boolean;
  required?: boolean;
  description?: string;
  instruction?: string;
  status?: string;
}

interface ActivityLogEntry {
  id: string;
  timestamp: Date;
  action: string;
  type: 'request' | 'response' | 'error' | 'success' | 'info';
  method?: string;
  url?: string;
  status?: number;
  headers?: any;
  body?: any;
  responseData?: any;
  details: any;
  domainId?: number;
  domainName?: string;
  duration?: number;
}

interface Step {
  id: string;
  title: string;
  description: string;
  status: 'pending' | 'active' | 'completed' | 'failed' | 'waiting';
  startTime?: Date;
  endTime?: Date;
  estimatedDuration?: number;
  errorMessage?: string;
  details?: string;
}

const DomainManagerSuper: React.FC = () => {
  const [domains, setDomains] = useState<Domain[]>([]);
  const [newDomain, setNewDomain] = useState('');
  const [newFromEmail, setNewFromEmail] = useState('');
  const [selectedDomain, setSelectedDomain] = useState<Domain | null>(null);
  const [dnsRecords, setDnsRecords] = useState<DNSRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'add' | 'verify' | 'debug'>('overview');
  const [activityLog, setActivityLog] = useState<ActivityLogEntry[]>([]);
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [currentStep, setCurrentStep] = useState<string>('idle');
  const [steps, setSteps] = useState<Step[]>([]);
  const [copiedText, setCopiedText] = useState<string | null>(null);

  // Initialize steps for domain addition
  const initializeSteps = (domainName?: string): Step[] => {
    const domain = domainName || 'example.com';
    return [
    {
      id: 'validate',
      title: 'Validate Domain Format (Browser/JavaScript)',
      description: `LOCAL: Running JavaScript regex test in your browser. Checking "${domain}" against pattern /^([a-z0-9]+(-[a-z0-9]+)*\\.)+[a-z]{2,}$/i. This happens instantly in memory.`,
      status: 'pending',
      estimatedDuration: 5,
      details: 'WHERE: Your browser (client-side JavaScript)\nWHEN: Instantly (<1ms)\nWHAT: Testing string against regex pattern\nNO NETWORK CALL - just local string validation',
    },
    {
      id: 'create',
      title: 'Insert into CloudFlare D1 Database',
      description: `NETWORK: POST http://localhost:8787/api/domains → CloudFlare Worker → D1 SQLite database. Sending JSON: {domain: "${domain}", userId: 1}. Network latency: ~20-50ms locally.`,
      status: 'pending',
      estimatedDuration: 100,
      details: 'WHERE: CloudFlare D1 (SQLite database)\nLOCATION: Running on localhost:8787\nACTUAL SQL: INSERT INTO domains (domain, user_id, verification_token) VALUES (?, ?, ?)\nRESPONSE: {domainId: 123, success: true}',
    },
    {
      id: 'generate-dns',
      title: 'Generate DNS Records (In Memory)',
      description: `LOCAL: JavaScript function generating DNS record objects in memory. No API calls, just creating JSON objects with verification tokens. Happens instantly.`,
      status: 'pending',
      estimatedDuration: 5,
      details: 'WHERE: CloudFlare Worker memory\nWHEN: <1ms (just creating objects)\nWHAT: Building array of DNS record objects\nNO DATABASE WRITE YET - just generating the records in memory',
    },
    {
      id: 'await-dns',
      title: 'MANUAL STEP: User Must Add DNS Records',
      description: `HUMAN ACTION REQUIRED: You need to login to your DNS provider and manually add records. This is NOT automated. DNS propagation is REAL and takes 5 minutes to 48 hours globally.`,
      status: 'pending',
      estimatedDuration: 300000, // 5 minutes minimum
      details: 'WHERE: Your DNS provider website (CloudFlare, GoDaddy, etc)\nWHO: YOU must do this manually\nTIME: 5-15 minutes typical, up to 48 hours\nWHY SO LONG: DNS servers worldwide must sync',
    },
    {
      id: 'verify',
      title: 'Check DNS (Simulated - Real Would Use dig/nslookup)',
      description: `NETWORK: POST http://localhost:8787/api/domains/${domain}/verify. In production: Would run actual DNS queries. Here: Using Math.random() to simulate 70% success rate.`,
      status: 'pending',
      estimatedDuration: 50,
      details: 'WHERE: CloudFlare Worker\nREAL WORLD: Would run: dig TXT _amazonses.${domain}\nOUR SIMULATION: Math.random() > 0.3 ? "verified" : "pending"\nACTUAL TIME: DNS lookup ~10-100ms',
    },
    {
      id: 'complete',
      title: 'Update Database Record to "Verified"',
      description: `DATABASE: UPDATE domains SET verification_status = "verified" WHERE id = ?. Single SQL update query to D1 database. Takes ~10-20ms.`,
      status: 'pending',
      estimatedDuration: 20,
      details: 'WHERE: CloudFlare D1 Database\nSQL: UPDATE domains SET verification_status = "verified", last_verified_at = datetime("now") WHERE id = ?\nRESULT: Domain can now send emails',
    },
  ];
  };

  // Log activity with full details
  const logActivity = useCallback((
    action: string,
    type: ActivityLogEntry['type'],
    details: any,
    additionalData?: Partial<ActivityLogEntry>
  ) => {
    const entry: ActivityLogEntry = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
      action,
      type,
      details,
      ...additionalData,
    };
    setActivityLog(prev => [entry, ...prev.slice(0, 99)]); // Keep last 100 entries
  }, []);

  // Wrapped API call with logging
  const apiCall = async (
    method: string,
    url: string,
    body?: any,
    headers?: any
  ): Promise<any> => {
    const startTime = Date.now();
    const requestId = `req-${Date.now()}`;

    // Log request
    logActivity(`${method} ${url}`, 'request', {
      method,
      url,
      body,
      headers,
      requestId,
    }, { method, url, body, headers });

    try {
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
        body: body ? JSON.stringify(body) : undefined,
      });

      const data = await response.json();
      const duration = Date.now() - startTime;

      // Log response
      logActivity(
        `Response ${response.status} - ${url}`,
        response.ok ? 'success' : 'error',
        {
          status: response.status,
          data,
          requestId,
          duration,
        },
        { status: response.status, responseData: data, duration }
      );

      return { response, data };
    } catch (error) {
      const duration = Date.now() - startTime;
      logActivity(
        `Failed ${method} ${url}`,
        'error',
        {
          error: error instanceof Error ? error.message : error,
          requestId,
          duration,
        },
        { duration }
      );
      throw error;
    }
  };

  // Fetch domains with logging
  const fetchDomains = async () => {
    try {
      const { data } = await apiCall('GET', 'http://localhost:8787/api/domains?userId=1');
      if (data.success) {
        setDomains(data.domains);
        logActivity('Fetched domains', 'success', { count: data.domains.length });
      }
    } catch (error) {
      logActivity('Failed to fetch domains', 'error', { error });
    }
  };

  useEffect(() => {
    fetchDomains();
  }, []);

  // Update step status
  const updateStepStatus = (stepId: string, status: Step['status'], errorMessage?: string) => {
    setSteps(prev => prev.map(step => {
      if (step.id === stepId) {
        const updatedStep = {
          ...step,
          status,
          errorMessage,
          endTime: status === 'completed' || status === 'failed' ? new Date() : undefined,
          startTime: status === 'active' ? new Date() : step.startTime,
        };

        // Log step updates
        logActivity(`Step ${stepId}: ${status}`, status === 'failed' ? 'error' : 'info', {
          step: updatedStep,
        });

        return updatedStep;
      }
      return step;
    }));

    if (status === 'active') {
      setCurrentStep(stepId);
    }
  };

  // Add domain with step visualization
  const addDomain = async () => {
    if (!newDomain) return;

    setLoading(true);
    const newSteps = initializeSteps(newDomain.toLowerCase().trim());
    setSteps(newSteps);
    setActiveTab('verify');

    try {
      // Step 1: Validate
      updateStepStatus('validate', 'active');

      // Log validation start
      logActivity(`Validating domain format: "${newDomain}"`, 'info', {
        domain: newDomain,
        checks: ['Format validation', 'TLD verification', 'Character validation']
      });

      await new Promise(resolve => setTimeout(resolve, 5)); // Regex is instant, just for UI feedback

      const domainRegex = /^([a-z0-9]+(-[a-z0-9]+)*\.)+[a-z]{2,}$/i;
      const validationChecks = {
        hasValidFormat: domainRegex.test(newDomain),
        hasValidTLD: newDomain.includes('.'),
        noSpaces: !newDomain.includes(' '),
        noSpecialChars: !/[^a-z0-9.-]/i.test(newDomain),
        notStartWithHyphen: !newDomain.startsWith('-'),
        notEndWithHyphen: !newDomain.endsWith('-')
      };

      logActivity('Domain validation results', 'info', validationChecks);

      if (!validationChecks.hasValidFormat) {
        const failureReason = !validationChecks.hasValidTLD ? 'Missing or invalid TLD' :
                             !validationChecks.noSpaces ? 'Contains spaces' :
                             !validationChecks.noSpecialChars ? 'Contains special characters' :
                             'Invalid domain format';

        updateStepStatus('validate', 'failed', `Validation failed: ${failureReason}`);
        logActivity('Domain validation failed', 'error', {
          domain: newDomain,
          reason: failureReason,
          checks: validationChecks
        });
        setLoading(false);
        return;
      }

      logActivity(`Domain "${newDomain}" passed all validation checks`, 'success', validationChecks);
      updateStepStatus('validate', 'completed');

      // Step 2: Create domain
      updateStepStatus('create', 'active');
      const { data } = await apiCall('POST', 'http://localhost:8787/api/domains', {
        domain: newDomain.toLowerCase().trim(),
        enableMailFrom: true,
        userId: 1,
        defaultFromEmail: newFromEmail || `noreply@${newDomain.toLowerCase().trim()}`,
      });

      if (data.success) {
        updateStepStatus('create', 'completed');

        // Step 3: Generate DNS records
        updateStepStatus('generate-dns', 'active');
        await new Promise(resolve => setTimeout(resolve, 5)); // Just creating objects in memory

        // Transform records for DNSVerificationTracker
        const transformedRecords = data.dnsRecords.map((record: any, index: number) => ({
          id: `${record.type}-${index}`,
          type: record.type as 'TXT' | 'CNAME' | 'MX' | 'A' | 'AAAA' | 'NS',
          name: record.name,
          value: record.value,
          ttl: record.ttl,
          priority: record.priority,
          required: record.required !== false,
          description: record.purpose || `${record.type} record for domain verification`,
        }));

        setDnsRecords(transformedRecords as any);
        updateStepStatus('generate-dns', 'completed');

        // Step 4: Wait for DNS
        updateStepStatus('await-dns', 'waiting');

        await fetchDomains();
        setNewDomain('');
        setNewFromEmail('');

        // Find and select the new domain
        const newDomainObj = domains.find(d => d.domain === newDomain.toLowerCase().trim());
        if (newDomainObj) {
          setSelectedDomain(newDomainObj);
        }
      } else {
        updateStepStatus('create', 'failed', data.error || 'Failed to add domain');
      }
    } catch (error) {
      logActivity('Failed to add domain', 'error', { error });
      updateStepStatus(currentStep || 'create', 'failed', 'Network error');
    } finally {
      setLoading(false);
    }
  };

  // Verify domain with step updates
  const verifyDomain = async (domainId: number) => {
    updateStepStatus('verify', 'active');

    try {
      const { data } = await apiCall('POST', `http://localhost:8787/api/domains/${domainId}/verify`);

      if (data.success && data.status === 'verified') {
        updateStepStatus('verify', 'completed');
        updateStepStatus('complete', 'completed');
        await fetchDomains();
      } else {
        updateStepStatus('verify', 'failed', data.message || 'Verification failed');
      }

      alert(data.message);
    } catch (error) {
      updateStepStatus('verify', 'failed', 'Verification check failed');
    }
  };

  // Retry failed step
  const retryStep = (stepId: string) => {
    logActivity(`Retrying step: ${stepId}`, 'info', { stepId });

    if (stepId === 'validate' || stepId === 'create' || stepId === 'generate-dns') {
      addDomain();
    } else if (stepId === 'verify' && selectedDomain) {
      verifyDomain(selectedDomain.id);
    }
  };

  // Copy to clipboard with feedback
  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopiedText(label);
    logActivity(`Copied ${label}`, 'info', { value: text });
    setTimeout(() => setCopiedText(null), 2000);
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-4xl font-bold mb-2 bg-gradient-to-r from-blue-400 to-purple-600 bg-clip-text text-transparent">
            🚀 Domain Manager Super
          </h1>
          <p className="text-gray-400">Complete domain management with step-by-step visibility</p>
        </motion.div>

        {/* Statistics Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8"
        >
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="text-2xl font-bold text-blue-400">{domains.length}</div>
            <div className="text-sm text-gray-400">Total Domains</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="text-2xl font-bold text-green-400">
              {domains.filter(d => d.verification_status === 'verified').length}
            </div>
            <div className="text-sm text-gray-400">Verified</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="text-2xl font-bold text-yellow-400">
              {domains.filter(d => d.verification_status === 'pending').length}
            </div>
            <div className="text-sm text-gray-400">Pending</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="text-2xl font-bold text-purple-400">{activityLog.length}</div>
            <div className="text-sm text-gray-400">Activities</div>
          </div>
        </motion.div>

        {/* Tab Navigation */}
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-4 py-2 rounded-lg font-bold transition ${
              activeTab === 'overview' ? 'bg-blue-600' : 'bg-gray-800 hover:bg-gray-700'
            }`}
          >
            📊 Overview
          </button>
          <button
            onClick={() => setActiveTab('add')}
            className={`px-4 py-2 rounded-lg font-bold transition ${
              activeTab === 'add' ? 'bg-blue-600' : 'bg-gray-800 hover:bg-gray-700'
            }`}
          >
            ➕ Add Domain
          </button>
          <button
            onClick={() => setActiveTab('verify')}
            className={`px-4 py-2 rounded-lg font-bold transition ${
              activeTab === 'verify' ? 'bg-blue-600' : 'bg-gray-800 hover:bg-gray-700'
            }`}
          >
            ✅ Verify & Track
          </button>
          <button
            onClick={() => setShowDebugPanel(!showDebugPanel)}
            className={`px-4 py-2 rounded-lg font-bold transition ${
              showDebugPanel ? 'bg-purple-600' : 'bg-gray-800 hover:bg-gray-700'
            }`}
          >
            🔍 Debug Panel
          </button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-4"
          >
            <div className="bg-gray-800 rounded-lg p-6">
              <h2 className="text-2xl font-bold mb-4">Your Domains</h2>

              {domains.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-400 mb-4">No domains configured yet</p>
                  <button
                    onClick={() => setActiveTab('add')}
                    className="px-6 py-2 bg-blue-600 rounded-lg font-bold hover:bg-blue-700 transition"
                  >
                    Add Your First Domain
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  {domains.map((domain) => (
                    <div
                      key={domain.id}
                      className="bg-gray-900 rounded-lg p-4 flex justify-between items-center"
                    >
                      <div>
                        <div className="text-lg font-semibold">{domain.domain}</div>
                        <div className="text-sm text-gray-400">
                          Status: <span className={
                            domain.verification_status === 'verified' ? 'text-green-400' :
                            domain.verification_status === 'pending' ? 'text-yellow-400' :
                            'text-red-400'
                          }>{domain.verification_status}</span>
                          {' • '}
                          DKIM: {domain.dkim_enabled ? '✅' : '❌'}
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            setSelectedDomain(domain);
                            setActiveTab('verify');
                          }}
                          className="px-3 py-1 bg-blue-600 rounded text-sm font-bold hover:bg-blue-700"
                        >
                          View DNS
                        </button>
                        {domain.verification_status !== 'verified' && (
                          <button
                            onClick={() => verifyDomain(domain.id)}
                            className="px-3 py-1 bg-green-600 rounded text-sm font-bold hover:bg-green-700"
                          >
                            Verify
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Add Domain Tab */}
        {activeTab === 'add' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            <div className="bg-gray-800 rounded-lg p-6">
              <h2 className="text-2xl font-bold mb-4">Add New Domain</h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Domain Name</label>
                  <input
                    type="text"
                    value={newDomain}
                    onChange={(e) => setNewDomain(e.target.value)}
                    placeholder="example.com"
                    className="w-full px-4 py-2 bg-gray-900 rounded-lg border border-gray-700 text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Default From Email (optional)</label>
                  <input
                    type="email"
                    value={newFromEmail}
                    onChange={(e) => setNewFromEmail(e.target.value)}
                    placeholder="noreply@example.com"
                    className="w-full px-4 py-2 bg-gray-900 rounded-lg border border-gray-700 text-white"
                  />
                </div>

                <button
                  onClick={addDomain}
                  disabled={!newDomain || loading}
                  className="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg font-bold hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 transition"
                >
                  {loading ? 'Processing...' : 'Add Domain & Generate DNS Records'}
                </button>
              </div>
            </div>

            {/* Step Visualizer */}
            {steps.length > 0 && (
              <StepVisualizer
                currentStep={currentStep}
                steps={steps}
                onRetry={retryStep}
                orientation="vertical"
              />
            )}
          </motion.div>
        )}

        {/* Verify & Track Tab */}
        {activeTab === 'verify' && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="space-y-6"
          >
            {selectedDomain ? (
              <>
                <div className="bg-gray-800 rounded-lg p-6">
                  <h2 className="text-2xl font-bold mb-2">{selectedDomain.domain}</h2>
                  <p className="text-gray-400">
                    Configure DNS records and track verification progress
                  </p>
                </div>

                <DNSVerificationTracker
                  domainId={selectedDomain.domain}
                  dnsRecords={dnsRecords as any}
                  onStatusChange={(recordId, status) => {
                    logActivity(`DNS record ${recordId} status: ${status}`, 'info', {
                      recordId,
                      status,
                    });
                  }}
                />
              </>
            ) : (
              <div className="bg-gray-800 rounded-lg p-8 text-center">
                <p className="text-gray-400 mb-4">Select a domain to track DNS verification</p>
                <button
                  onClick={() => setActiveTab('overview')}
                  className="px-6 py-2 bg-blue-600 rounded-lg font-bold hover:bg-blue-700"
                >
                  View Domains
                </button>
              </div>
            )}
          </motion.div>
        )}

        {/* Debug Panel - Floating */}
        {showDebugPanel && (
          <DomainDebugPanel activityLog={activityLog as any} />
        )}
      </div>
    </div>
  );
};

export default DomainManagerSuper;